import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(date));
}

export function formatDateTime(date: string | Date | undefined | null): string {
  if (!date) return "—";
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

export function formatPhone(phone: string): string {
  // Format Polish phone number
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }
  return phone;
}

export function formatPercent(value: number, decimals = 2): string {
  return `${value.toFixed(decimals)}%`;
}

export function getCreditTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    gotowkowy: "Gotówkowy",
    konsolidacyjny: "Konsolidacyjny",
    hipoteczny: "Hipoteczny",
  };
  return labels[type] || type;
}

export function getStatusBadgeClass(status: string): string {
  const classes: Record<string, string> = {
    new: "badge-new",
    reserved: "badge-reserved",
    active: "badge-active",
    pending: "badge-pending",
    suspended: "badge-suspended",
    completed: "badge-new",
    expired: "badge-suspended",
    sent: "badge-active",
    accepted: "badge-new",
    rejected: "badge-suspended",
    draft: "badge-pending",
  };
  return classes[status] || "badge-pending";
}

export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    new: "Nowy",
    reserved: "Zarezerwowany",
    active: "Aktywny",
    waiting_for_client: "Oczekuje na klienta",
    in_session: "W sesji",
    pending: "Oczekujący",
    pending_consent: "Oczekuje na zgodę",
    revoked_by_client: "Cofnięta zgoda",
    suspended: "Zawieszony",
    completed: "Zakończony",
    expired: "Wygasły",
    sent: "Wysłana",
    accepted: "Zaakceptowana",
    rejected: "Odrzucona",
    draft: "Szkic",
  };
  return labels[status] || status;
}

export function maskPhone(phone: string): string {
  // Mask phone number for privacy: 123 456 789 -> 123 *** 789
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length >= 6) {
    return `${cleaned.slice(0, 3)} *** ${cleaned.slice(-3)}`;
  }
  return "*** *** ***";
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + "...";
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export type BadgeVariant = "default" | "success" | "warning" | "error" | "info";

export function getStatusVariant(status: string): BadgeVariant {
  const statusMap: Record<string, BadgeVariant> = {
    new: "success",
    active: "info",
    waiting_for_client: "warning",
    in_session: "info",
    pending: "default",
    pending_consent: "warning",
    reserved: "warning",
    completed: "success",
    expired: "error",
    suspended: "error",
    revoked_by_client: "error",
    sent: "info",
    accepted: "success",
    rejected: "error",
    draft: "default",
  };

  return statusMap[status] || "default";
}
